import { strFromU8, unzipSync } from 'fflate';

export interface NRELWeatherData {
  latitude: number;
  longitude: number;
  monthlyGHI: number[]; // kWh/m2/day for each month
  monthlyDNI: number[]; // kWh/m2/day for each month
  monthlyDHI: number[]; // kWh/m2/day for each month
  monthlyTemp: number[]; // C monthly mean
  monthlyWindSpeed: number[]; // m/s monthly mean
  annualGHI: number; // kWh/m2/year
  source: 'nrel';
  meta?: {
    names: string;
    intervalMinutes: number;
    endpoint: string;
  };
}

interface NRELMsgJobResponse {
  errors?: unknown;
  outputs?: {
    downloadUrl?: unknown;
  };
}

const NSRDB_MSG_ENDPOINT = 'https://developer.nrel.gov/api/nsrdb/v2/solar/nsrdb-msg-v1-0-0-download.json';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000;
const DEFAULT_NAMES = '2022';
const DEFAULT_INTERVAL_MINUTES = 60;
const DEFAULT_MAX_WAIT_MINUTES = 20;
const DEFAULT_RETRY_SECONDS = 5;
const REQUEST_TIMEOUT_MS = 30_000;
const RETRYABLE_DOWNLOAD_STATUSES = new Set([403, 404, 425, 429, 500, 502, 503, 504]);

const nrelCache = new Map<string, { data: NRELWeatherData; timestamp: number }>();

function readEnv(name: string): string | undefined {
  const viteValue = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env?.[name];
  if (typeof viteValue === 'string' && viteValue.trim()) return viteValue.trim();

  if (typeof process !== 'undefined' && (process as { env?: Record<string, string | undefined> }).env) {
    const processValue = (process as { env?: Record<string, string | undefined> }).env?.[name];
    if (typeof processValue === 'string' && processValue.trim()) return processValue.trim();
  }

  return undefined;
}

function readPositiveIntEnv(name: string, fallback: number): number {
  const value = readEnv(name);
  if (!value) return fallback;

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new Error(`Invalid ${name}. Expected a positive number.`);
  }

  return Math.round(parsed);
}

/**
 * Returns the base URL for the NREL proxy.
 * - If VITE_SUPABASE_URL is set: Supabase Edge Function (works in dev and prod)
 * - Fallback: Vite dev middleware at /nrel-proxy
 */
function getProxyBaseUrl(): string {
  const supabaseUrl = readEnv('VITE_SUPABASE_URL');
  if (supabaseUrl) {
    return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/nrel-proxy`;
  }

  return '/nrel-proxy';
}

function getProxyRequestHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const anonKey = readEnv('VITE_SUPABASE_ANON_KEY');
  if (anonKey) {
    headers.Authorization = `Bearer ${anonKey}`;
  }

  return headers;
}

function parseApiErrors(errors: unknown): string[] {
  if (!Array.isArray(errors)) return [];

  return errors
    .map((error) => {
      if (typeof error === 'string') return error;
      if (error && typeof error === 'object') {
        const maybeMessage = (error as { message?: unknown }).message;
        if (typeof maybeMessage === 'string') return maybeMessage;
      }
      return String(error);
    })
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

async function readResponseText(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return '';
  }
}

async function fetchWithTimeout(
  url: string,
  init?: RequestInit,
  timeoutMs: number = REQUEST_TIMEOUT_MS
): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      ...init,
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`NREL request timed out after ${timeoutMs} ms.`);
    }

    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`NREL network request failed: ${message}`);
  } finally {
    clearTimeout(timeout);
  }
}

function parseRetryAfterMs(retryAfterHeader: string | null, fallbackMs: number): number {
  if (!retryAfterHeader) return fallbackMs;

  const asNumber = Number(retryAfterHeader);
  if (Number.isFinite(asNumber) && asNumber >= 0) {
    return asNumber * 1000;
  }

  const asDate = Date.parse(retryAfterHeader);
  if (Number.isFinite(asDate)) {
    const deltaMs = asDate - Date.now();
    return deltaMs > 0 ? deltaMs : fallbackMs;
  }

  return fallbackMs;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, Math.max(0, ms)));
}

async function requestDownloadUrl(params: {
  latitude: number;
  longitude: number;
  names: string;
  intervalMinutes: number;
}): Promise<string> {
  const proxyUrl = getProxyBaseUrl();

  const response = await fetchWithTimeout(proxyUrl, {
    method: 'POST',
    headers: getProxyRequestHeaders(),
    body: JSON.stringify({
      action: 'submit-job',
      latitude: params.latitude,
      longitude: params.longitude,
      names: params.names,
      intervalMinutes: params.intervalMinutes,
    }),
  });

  const bodyText = await readResponseText(response);

  if (!response.ok) {
    throw new Error(
      `NREL job request failed (${response.status} ${response.statusText})${bodyText ? `: ${bodyText}` : ''}`
    );
  }

  let payload: NRELMsgJobResponse;
  try {
    payload = JSON.parse(bodyText) as NRELMsgJobResponse;
  } catch {
    throw new Error('NREL job response was not valid JSON.');
  }

  const apiErrors = parseApiErrors(payload.errors);
  if (apiErrors.length > 0) {
    throw new Error(`NREL returned errors for this location: ${apiErrors.join(' | ')}`);
  }

  const downloadUrl = payload.outputs?.downloadUrl;
  if (typeof downloadUrl !== 'string' || !downloadUrl.trim()) {
    throw new Error('NREL job response missing outputs.downloadUrl.');
  }

  return downloadUrl;
}

async function pollAndDownloadZip(params: {
  downloadUrl: string;
  maxWaitMinutes: number;
  retrySeconds: number;
}): Promise<Uint8Array> {
  const proxyUrl = getProxyBaseUrl();
  const maxWaitMs = params.maxWaitMinutes * 60 * 1000;
  const fallbackRetryMs = params.retrySeconds * 1000;
  const deadline = Date.now() + maxWaitMs;

  while (Date.now() <= deadline) {
    const response = await fetchWithTimeout(proxyUrl, {
      method: 'POST',
      headers: getProxyRequestHeaders(),
      body: JSON.stringify({ action: 'download', url: params.downloadUrl }),
    });

    if (response.status === 200) {
      const zipped = new Uint8Array(await response.arrayBuffer());
      if (zipped.length === 0) {
        throw new Error('NREL download returned an empty ZIP payload.');
      }
      return zipped;
    }

    const bodyText = await readResponseText(response);

    if (RETRYABLE_DOWNLOAD_STATUSES.has(response.status)) {
      const normalizedBody = bodyText.toLowerCase();
      if (response.status === 403 && normalizedBody.includes('not allowed')) {
        throw new Error(
          `NREL download URL was rejected by proxy allowlist (${response.status} ${response.statusText})${bodyText ? `: ${bodyText}` : ''}`
        );
      }

      const retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'), fallbackRetryMs);
      await wait(retryAfterMs);
      continue;
    }

    throw new Error(
      `NREL download polling failed (${response.status} ${response.statusText})${bodyText ? `: ${bodyText}` : ''}`
    );
  }

  throw new Error(`Timed out waiting for NREL NSRDB download URL after ${params.maxWaitMinutes} minutes.`);
}

function extractFirstCsvFromZip(zipBytes: Uint8Array): string {
  let unzipped: Record<string, Uint8Array>;
  try {
    unzipped = unzipSync(zipBytes);
  } catch (error) {
    throw new Error(`Failed to extract NREL ZIP: ${error instanceof Error ? error.message : String(error)}`);
  }

  const csvEntry = Object.entries(unzipped).find(([name]) => name.toLowerCase().endsWith('.csv'));
  if (!csvEntry) {
    throw new Error('NREL ZIP did not contain a CSV file.');
  }

  return strFromU8(csvEntry[1]);
}

function parseCsvLine(line: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];

    if (char === '"') {
      const next = line[index + 1];
      if (inQuotes && next === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      values.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current);
  return values;
}

function colIndex(header: string[], ...aliases: string[]): number {
  const normalizedHeader = header.map((value) => value.trim().toLowerCase());
  for (const alias of aliases) {
    const index = normalizedHeader.indexOf(alias.trim().toLowerCase());
    if (index >= 0) return index;
  }
  return -1;
}

function findHeaderIndex(lines: string[]): number {
  const upperBound = Math.min(lines.length, 250);

  for (let lineIndex = 0; lineIndex < upperBound; lineIndex += 1) {
    const columns = parseCsvLine(lines[lineIndex]).map((value) => value.trim().toLowerCase());
    const hasMonth = columns.includes('month');
    const hasDay = columns.includes('day');
    const hasHour = columns.includes('hour') || columns.includes('hour (local)') || columns.includes('hour (utc)');
    const hasIrradiance = columns.includes('ghi') && columns.includes('dni') && columns.includes('dhi');

    if (hasMonth && hasDay && hasHour && hasIrradiance) {
      return lineIndex;
    }
  }

  return -1;
}

function assertMonthlySeries(values: number[], label: string): number[] {
  if (values.length !== 12) {
    throw new Error(`Expected 12 monthly values for ${label}, got ${values.length}.`);
  }

  if (values.some((value) => !Number.isFinite(value))) {
    throw new Error(`Monthly ${label} contains non-finite values.`);
  }

  return values;
}

function aggregateMonthly(csvText: string, intervalMinutes: number): Omit<NRELWeatherData, 'latitude' | 'longitude' | 'source'> {
  if (!Number.isFinite(intervalMinutes) || intervalMinutes <= 0) {
    throw new Error('Invalid intervalMinutes for aggregation.');
  }

  const dtHours = intervalMinutes / 60;
  const lines = csvText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const headerIndex = findHeaderIndex(lines);
  if (headerIndex < 0) {
    throw new Error('NSRDB CSV parsing failed: header row not found.');
  }

  const header = parseCsvLine(lines[headerIndex]).map((value) => value.trim());
  const monthIdx = colIndex(header, 'month');
  const dayIdx = colIndex(header, 'day');
  const ghiIdx = colIndex(header, 'ghi');
  const dniIdx = colIndex(header, 'dni');
  const dhiIdx = colIndex(header, 'dhi');
  const tempIdx = colIndex(header, 'air_temperature', 'temperature', 'temp', 'air temperature');
  const windIdx = colIndex(header, 'wind_speed', 'wind speed', 'windspeed');

  const missingColumns: string[] = [];
  if (monthIdx < 0) missingColumns.push('Month');
  if (dayIdx < 0) missingColumns.push('Day');
  if (ghiIdx < 0) missingColumns.push('GHI');
  if (dniIdx < 0) missingColumns.push('DNI');
  if (dhiIdx < 0) missingColumns.push('DHI');
  if (tempIdx < 0) missingColumns.push('air_temperature');
  if (windIdx < 0) missingColumns.push('wind_speed');

  if (missingColumns.length > 0) {
    throw new Error(`NSRDB CSV missing required columns: ${missingColumns.join(', ')}`);
  }

  const monthTotalsGHI = Array(12).fill(0);
  const monthTotalsDNI = Array(12).fill(0);
  const monthTotalsDHI = Array(12).fill(0);
  const tempSums = Array(12).fill(0);
  const tempCounts = Array(12).fill(0);
  const windSums = Array(12).fill(0);
  const windCounts = Array(12).fill(0);
  const ghiCounts = Array(12).fill(0);
  const dniCounts = Array(12).fill(0);
  const dhiCounts = Array(12).fill(0);
  const daysByMonth = Array.from({ length: 12 }, () => new Set<number>());

  for (let lineIndex = headerIndex + 1; lineIndex < lines.length; lineIndex += 1) {
    const row = parseCsvLine(lines[lineIndex]);
    const month = Number(row[monthIdx]);
    const day = Number(row[dayIdx]);

    if (!Number.isInteger(month) || month < 1 || month > 12) {
      continue;
    }

    if (!Number.isInteger(day) || day < 1 || day > 31) {
      continue;
    }

    const monthIndex = month - 1;
    daysByMonth[monthIndex].add(day);

    const ghi = Number(row[ghiIdx]);
    const dni = Number(row[dniIdx]);
    const dhi = Number(row[dhiIdx]);
    const temperature = Number(row[tempIdx]);
    const wind = Number(row[windIdx]);

    if (Number.isFinite(ghi)) {
      monthTotalsGHI[monthIndex] += (ghi * dtHours) / 1000;
      ghiCounts[monthIndex] += 1;
    }

    if (Number.isFinite(dni)) {
      monthTotalsDNI[monthIndex] += (dni * dtHours) / 1000;
      dniCounts[monthIndex] += 1;
    }

    if (Number.isFinite(dhi)) {
      monthTotalsDHI[monthIndex] += (dhi * dtHours) / 1000;
      dhiCounts[monthIndex] += 1;
    }

    if (Number.isFinite(temperature)) {
      tempSums[monthIndex] += temperature;
      tempCounts[monthIndex] += 1;
    }

    if (Number.isFinite(wind)) {
      windSums[monthIndex] += wind;
      windCounts[monthIndex] += 1;
    }
  }

  const monthlyGHI = Array(12).fill(Number.NaN);
  const monthlyDNI = Array(12).fill(Number.NaN);
  const monthlyDHI = Array(12).fill(Number.NaN);
  const monthlyTemp = Array(12).fill(Number.NaN);
  const monthlyWindSpeed = Array(12).fill(Number.NaN);

  for (let monthIndex = 0; monthIndex < 12; monthIndex += 1) {
    const daysInMonth = daysByMonth[monthIndex].size;

    if (daysInMonth === 0) {
      throw new Error(`Incomplete NSRDB data: month ${monthIndex + 1} has no day samples.`);
    }

    if (ghiCounts[monthIndex] === 0 || dniCounts[monthIndex] === 0 || dhiCounts[monthIndex] === 0) {
      throw new Error(`Incomplete NSRDB irradiance data for month ${monthIndex + 1}.`);
    }

    if (tempCounts[monthIndex] === 0 || windCounts[monthIndex] === 0) {
      throw new Error(`Incomplete NSRDB meteorological data for month ${monthIndex + 1}.`);
    }

    monthlyGHI[monthIndex] = monthTotalsGHI[monthIndex] / daysInMonth;
    monthlyDNI[monthIndex] = monthTotalsDNI[monthIndex] / daysInMonth;
    monthlyDHI[monthIndex] = monthTotalsDHI[monthIndex] / daysInMonth;
    monthlyTemp[monthIndex] = tempSums[monthIndex] / tempCounts[monthIndex];
    monthlyWindSpeed[monthIndex] = windSums[monthIndex] / windCounts[monthIndex];
  }

  const annualGHI = monthTotalsGHI.reduce((sum, monthlyTotal) => sum + monthlyTotal, 0);

  return {
    monthlyGHI: assertMonthlySeries(monthlyGHI, 'GHI'),
    monthlyDNI: assertMonthlySeries(monthlyDNI, 'DNI'),
    monthlyDHI: assertMonthlySeries(monthlyDHI, 'DHI'),
    monthlyTemp: assertMonthlySeries(monthlyTemp, 'temperature'),
    monthlyWindSpeed: assertMonthlySeries(monthlyWindSpeed, 'wind speed'),
    annualGHI,
    meta: {
      names: '',
      intervalMinutes,
      endpoint: NSRDB_MSG_ENDPOINT,
    },
  };
}

export async function fetchNRELWeatherData(latitude: number, longitude: number): Promise<NRELWeatherData> {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    throw new Error('Invalid coordinates for NREL weather request.');
  }

  const names = readEnv('VITE_NREL_NAMES') ?? DEFAULT_NAMES;
  const intervalMinutes = readPositiveIntEnv('VITE_NREL_INTERVAL_MINUTES', DEFAULT_INTERVAL_MINUTES);
  const maxWaitMinutes = readPositiveIntEnv('VITE_NREL_MAX_WAIT_MIN', DEFAULT_MAX_WAIT_MINUTES);
  const retrySeconds = readPositiveIntEnv('VITE_NREL_RETRY_SECONDS', DEFAULT_RETRY_SECONDS);

  const cacheKey = `${latitude.toFixed(4)},${longitude.toFixed(4)}|names=${names}|interval=${intervalMinutes}`;
  const cached = nrelCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
    return cached.data;
  }

  const downloadUrl = await requestDownloadUrl({
    latitude,
    longitude,
    names,
    intervalMinutes,
  });

  const zipBytes = await pollAndDownloadZip({
    downloadUrl,
    maxWaitMinutes,
    retrySeconds,
  });

  const csvText = extractFirstCsvFromZip(zipBytes);
  const aggregated = aggregateMonthly(csvText, intervalMinutes);

  const weatherData: NRELWeatherData = {
    latitude,
    longitude,
    monthlyGHI: aggregated.monthlyGHI,
    monthlyDNI: aggregated.monthlyDNI,
    monthlyDHI: aggregated.monthlyDHI,
    monthlyTemp: aggregated.monthlyTemp,
    monthlyWindSpeed: aggregated.monthlyWindSpeed,
    annualGHI: aggregated.annualGHI,
    source: 'nrel',
    meta: {
      names,
      intervalMinutes,
      endpoint: NSRDB_MSG_ENDPOINT,
    },
  };

  nrelCache.set(cacheKey, { data: weatherData, timestamp: Date.now() });

  console.log('[NREL] Weather array for', latitude.toFixed(4), longitude.toFixed(4), ':', {
    monthlyGHI: weatherData.monthlyGHI,
    monthlyDNI: weatherData.monthlyDNI,
    monthlyDHI: weatherData.monthlyDHI,
    monthlyTemp: weatherData.monthlyTemp,
    monthlyWindSpeed: weatherData.monthlyWindSpeed,
    annualGHI: weatherData.annualGHI,
  });

  return weatherData;
}

export function clearNRELCache(): void {
  nrelCache.clear();
}

export function getNRELCacheStats(): { size: number; entries: string[] } {
  return {
    size: nrelCache.size,
    entries: Array.from(nrelCache.keys()),
  };
}
